"use client"

import { useState, useEffect } from "react"
import { Plus, FilePenLine, Trash, Search } from "lucide-react"
import Modal from "../../components/Modal"
import { getEmpresas, createEmpresa, updateEmpresa } from "../../services/empresaService"
import "./Empresa.css"

const Empresa = () => {
  const [empresas, setEmpresas] = useState([])
  const [formData, setFormData] = useState({
    id: "",
    nombre: "",
    telefono: "",
    email: "",
    estado: "0",
  })
  const [editing, setEditing] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [empresaToDelete, setEmpresaToDelete] = useState(null)
  const [filter, setFilter] = useState("active")
  const [searchTerm, setSearchTerm] = useState("")

  useEffect(() => {
    fetchAllEmpresas()
  }, [])

  const fetchAllEmpresas = async () => {
    try {
      const data = await getEmpresas()
      setEmpresas(data)
    } catch (error) {
      console.error(error.message)
    }
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (editing) {
      try {
        await updateEmpresa(formData.id, formData)
        fetchAllEmpresas()
        closeModal()
      } catch (error) {
        console.error(error.message)
      }
    } else {
      try {
        await createEmpresa(formData)
        fetchAllEmpresas()
        closeModal()
      } catch (error) {
        console.error(error.message)
      }
    }
  }

  const handleNew = () => {
    setFormData({
      id: "",
      nombre: "",
      telefono: "",
      email: "",
      estado: "0",
    })
    setEditing(false)
    setIsModalOpen(true)
  }

  const handleEdit = (empresa) => {
    setFormData({
      id: empresa.id,
      nombre: empresa.nombre,
      telefono: empresa.telefono,
      email: empresa.email,
      estado: empresa.estado.toString(),
    })
    setEditing(true)
    setIsModalOpen(true)
  }

  const openDeleteConfirmation = (empresa) => {
    setEmpresaToDelete(empresa)
  }

  const confirmDelete = async () => {
    if (empresaToDelete) {
      const updatedEmpresa = { ...empresaToDelete, estado: "1" }
      try {
        await updateEmpresa(empresaToDelete.id, updatedEmpresa)
        fetchAllEmpresas()
      } catch (error) {
        console.error(error.message)
      }
      closeDeleteModal()
    }
  }

  const closeDeleteModal = () => {
    setEmpresaToDelete(null)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const handleFilterChange = (e) => {
    setFilter(e.target.value)
  }

  const filteredEmpresas = empresas
    .filter((empresa) => {
      if (filter === "active") {
        return empresa.estado === 0 || empresa.estado === "0"
      }
      if (filter === "inactive") {
        return empresa.estado === 1 || empresa.estado === "1"
      }
      return true
    })
    .filter((empresa) => {
      if (!searchTerm) return true
      const lowerCaseSearch = searchTerm.toLowerCase()
      return (
        empresa.nombre.toLowerCase().includes(lowerCaseSearch) ||
        empresa.telefono.toLowerCase().includes(lowerCaseSearch) ||
        empresa.email.toLowerCase().includes(lowerCaseSearch)
      )
    })

  return (
    <div className="empresa-container">
      <div className="empresa-header">
        <h1 className="empresa-title">Gestión de Empresas</h1>
        <div className="empresa-header-decoration"></div>
      </div>

      <div className="empresa-table-container">
        <div className="empresa-table-header">
          <div className="empresa-table-controls">
            <div className="empresa-filter-container">
              <label>Estado:</label>
              <select onChange={handleFilterChange} value={filter}>
                <option value="active">Activas</option>
                <option value="inactive">Inactivas</option>
                <option value="all">Todas</option>
              </select>
            </div>
            <div className="empresa-search-bar-container">
              <Search size={16} className="empresa-search-bar-icon" />
              <input
                type="text"
                className="empresa-search-bar-input"
                placeholder="Buscar por nombre, teléfono o email..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          <div>
            <button onClick={handleNew} className="empresa-btn-primary">
              <Plus size={16} />
              Nueva Empresa
            </button>
          </div>
        </div>

        <div className="empresa-table-responsive">
          <table className="empresa-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Teléfono</th>
                <th>Email</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmpresas.length > 0 ? (
                filteredEmpresas.map((empresa) => (
                  <tr key={empresa.id}>
                    <td>
                      <span className="empresa-id-badge">{empresa.id}</span>
                    </td>
                    <td>{empresa.nombre}</td>
                    <td>{empresa.telefono}</td>
                    <td>{empresa.email}</td>
                    <td>
                      <span
                        className={`empresa-estado-badge ${
                          empresa.estado === 0 || empresa.estado === "0" ? "activo" : "inactivo"
                        }`}
                      >
                        {empresa.estado === 0 || empresa.estado === "0" ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td>
                      <div className="empresa-action-buttons">
                        <button onClick={() => handleEdit(empresa)} className="empresa-action-button">
                          <FilePenLine size={18} className="empresa-edit-icon" />
                        </button>
                        {(empresa.estado === 0 || empresa.estado === "0") && (
                          <button onClick={() => openDeleteConfirmation(empresa)} className="empresa-action-button">
                            <Trash size={18} className="empresa-delete-icon" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">
                    <div className="empresa-empty-message">
                      {searchTerm ? "No se encontraron empresas con ese criterio" : "No hay empresas registradas"}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <div className="empresa-modal-header">
          <h2 className="empresa-modal-title">{editing ? "Actualizar Empresa" : "Crear Empresa"}</h2>
        </div>
        <div className="empresa-modal-content">
          <form onSubmit={handleSubmit}>
            <div className="empresa-modal-form-group">
              <label className="empresa-modal-label">Nombre:</label>
              <input
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                required
                className="empresa-modal-input"
              />
            </div>
            <div className="empresa-modal-form-group">
              <label className="empresa-modal-label">Teléfono:</label>
              <input
                type="text"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                required
                className="empresa-modal-input"
              />
            </div>
            <div className="empresa-modal-form-group">
              <label className="empresa-modal-label">Email:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="empresa-modal-input"
              />
            </div>
            <div className="empresa-modal-form-group">
              <label className="empresa-modal-label">Estado:</label>
              <select name="estado" value={formData.estado} onChange={handleChange} required className="empresa-modal-select">
                <option value="0">Activo</option>
                <option value="1">Inactivo</option>
              </select>
            </div>
            <div className="empresa-modal-actions">
              <button type="submit" className="empresa-modal-btn empresa-modal-btn-primary">
                {editing ? "Actualizar" : "Guardar"}
              </button>
              <button type="button" onClick={closeModal} className="empresa-modal-btn empresa-modal-btn-secondary">
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal isOpen={empresaToDelete !== null} onClose={closeDeleteModal}>
        <div className="empresa-modal-header">
          <h2 className="empresa-modal-title">Confirmación</h2>
        </div>
        <div className="empresa-modal-content">
          <p>
            ¿Está seguro que desea eliminar la empresa <strong>{empresaToDelete && empresaToDelete.nombre}</strong>?
          </p>
          <div className="empresa-modal-actions">
            <button onClick={confirmDelete} className="empresa-modal-btn empresa-modal-btn-primary">
              Confirmar
            </button>
            <button onClick={closeDeleteModal} className="empresa-modal-btn empresa-modal-btn-secondary">
              Cancelar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default Empresa